import React, { useEffect, useState } from 'react';
import styles from '../insertsPages.module.css';
import Tabs from '../../../Tabs';
import { FileInput } from '../../../../../../personalisation/src/components/FileInput';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserImageArea, VisualiserTextBox, VisualiserGroup } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantForProduct } from '../../../../utilities/visualiserVariants.js';

const tabConfig = [
  { id: 'FRONT', label: 'Front' },
  { id: 'INSIDE', label: 'Inside' },
  { id: 'BACK', label: 'Back' }
];

/**
 * CardPhotoUpload component allows users to upload a photo for their greeting card.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function CardPhotoUpload({ ...rest }) {
  const {
    getImageInput,
    setImageInput,
    getTextInput,
    setTextInput,
    getTransformation,
    setTransformation,
    setVisualiserSelection,
    clearVisualiserSelection
  } = useUserInput();
  const [activeIndex, setActiveIndex] = useState(0);

  const frontImageKey = 'cardPhotoUpload.frontImage';
  const frontImage = getImageInput(frontImageKey);
  const frontTransformation = getTransformation(frontImageKey);

  useEffect(() => {
    const productKey = 'customGreetingsCards';
    const product = getInsertsAndCardsProduct({ productKey });

    // Match the known "Image Upload" variant for custom greetings cards.
    const variantTitle = 'Image Upload';
    const variant = findVariantForProduct({ product, variantTitle, fallbackOption1: 'Image Upload' });

    setVisualiserSelection({
      productKey,
      variant,
      config: {
        templateId: 'CARD_CUSTOM_IMAGETEXT',
        mapping: {
          text: {
            'cardPhotoUpload.front': 'UVFrontText',
            'cardPhotoUpload.inside': 'UVInsideText'
          },
          images: {
            'cardPhotoUpload.frontImage': 'UVFrontImage'
          }
        }
      }
    });

    return () => {
      clearVisualiserSelection();
    };
  }, [setVisualiserSelection, clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs tabs={tabConfig} activeIndex={activeIndex} onChange={setActiveIndex} fullWidth={true} />

        {activeIndex === 0 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Upload an image below">
            <VisualiserImageArea
              value={frontImage}
              size={{
                width: '190px',
                height: '215px'
              }}
              position={{
                top: '10px',
                left: '50%'
              }}
              borderRadius="0"
              backgroundColor="var(--color-white)"
              style={{ 
                border: '0.1rem solid rgba(0, 0, 0, 0.3)',
                transform: 'translateX(-50%)'
              }}
              alt="Greeting card photo upload preview"
              scale={frontTransformation.scale}
              x={frontTransformation.x}
              y={frontTransformation.y}
              onUpdate={(updates) => setTransformation(frontImageKey, updates)}
            />
            <VisualiserTextBox
              size={{
                width: '190px',
                minWidth: '190px',
                maxWidth: '190px',
                minHeight: '40px',
                maxHeight: '65px'
              }}
              position={{
                top: '230px',
                left: '50%'
              }}
              font="Amalfi Coast"
              fontSize={21}
              lineHeight={1.7}
              fontWeight="600"
              maxLength={32}
              maxLines={2}
              style={{
                transform: 'translateX(-50%)'
              }}
              value={getTextInput('cardPhotoUpload.front')}
              setValue={((value) => setTextInput('cardPhotoUpload.front', value))}
            />
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <VisualiserGroup>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
              {' '}
            </Visualiser>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
              <VisualiserTextBox
                size={{
                  width: '190px',
                  minWidth: '150px',
                  maxWidth: '190px',
                  minHeight: '50px',
                  maxHeight: '260px'
                }}
                position={{
                  top: '50%',
                  left: '50%'
                }}
                maxLength={150}
                maxLines={12}
                lineHeight={1.2}
                fontSize={16}
                style={{
                  transform: 'translate(-50%, -50%)'
                }}
                value={getTextInput('cardPhotoUpload.inside')}
                setValue={((value) => setTextInput('cardPhotoUpload.inside', value))}
              />
            </Visualiser>
          </VisualiserGroup>
        )}

        {activeIndex === 2 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
            {' '}
          </Visualiser>
        )}

        {activeIndex === 0 && (
          <div className={styles['file-input-container']}>
          <FileInput
            label="Upload a Photo"
            onFileSelect={(value) => setImageInput(frontImageKey, value)}
            accept={'png,jpg,jpeg,webp'}
            disabled={false}
            buttonVariant="primary"
            buttonColor="orange"
            buttonSize="small"
            maxFileSize={5 * 1024 * 1024}
            id="front-image"
            selectedFile={
              frontImage && frontImage.userImage && frontImage.userImage.file instanceof File ? frontImage.userImage.file
              : null
            }
            />
        </div>
        )}
      </div>
    </div>
  );
}
